import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { apiClient } from "@/lib/api";
import SEO from "@/components/SEO";
import { SITE_URL, SITE_BRAND } from "@/lib/seo-config";
import { TRAVAUX_LIST } from "@/lib/travaux-types";
import { ShieldCheck, BadgeCheck, Award, MapPin, ChevronRight, Crown, Star, Building2, HardHat, ArrowRight } from "lucide-react";
import CityProspectForm from "@/components/CityProspectForm";

const fmtCompactEUR = (n) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(".0", "")} M€`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)} k€`;
  return `${Math.round(n)} €`;
};

// Build SEO config dynamically per city
function buildCitySEO(data) {
  if (!data) return null;
  const city = data.city_label;
  const slug = data.slug || "";
  const count = data.artisans_count || 0;
  return {
    title: `Artisans BTP à ${city} · ${count} pros SIRET vérifiés · Paiement sécurisé · Batisur`,
    description: `Trouvez un artisan de confiance à ${city} : plombier, électricien, maçon, peintre, carreleur, chauffagiste, couvreur, menuisier. ${count} professionnels SIRET vérifiés Sirene/INSEE. Paiement sécurisé en séquestre Stripe, certificats opposables, avis vérifiés. Devis gratuits.`,
    keywords: [
      `artisan ${city}`, `plombier ${city}`, `électricien ${city}`, `maçon ${city}`,
      `peintre ${city}`, `carreleur ${city}`, `chauffagiste ${city}`, `couvreur ${city}`,
      `menuisier ${city}`, `plaquiste ${city}`, `isolation ${city}`, `rénovation ${city}`,
      `artisan RGE ${city}`, `travaux ${city}`, `artisan SIRET vérifié ${city}`,
      `annuaire artisan ${city}`, `trouver artisan de confiance ${city}`,
      `devis travaux gratuit ${city}`, `rénovation salle de bain ${city}`,
      `rénovation cuisine ${city}`, `extension maison ${city}`,
      `paiement sécurisé travaux ${city}`, `séquestre travaux ${city}`,
      "artisan certifié France", "artisan avis vérifiés", "Batisur Connect",
    ].join(", "),
    canonical: `/artisans/ville/${slug}`,
    ogType: "website",
    schema: [
      {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": `${SITE_BRAND} · Artisans BTP à ${city}`,
        "url": `${SITE_URL}/artisans/ville/${slug}`,
        "description": `Annuaire local des artisans du bâtiment vérifiés SIRET à ${city}. Paiement sécurisé en séquestre Stripe.`,
        "areaServed": {
          "@type": "City",
          "name": city
        },
        "priceRange": "€€",
        "aggregateRating": data.avg_rating ? {
          "@type": "AggregateRating",
          "ratingValue": Number(data.avg_rating).toFixed(1),
          "reviewCount": data.local_completed_missions || 1,
          "bestRating": "5"
        } : undefined
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Accueil", "item": SITE_URL },
          { "@type": "ListItem", "position": 2, "name": "Artisans", "item": `${SITE_URL}/artisans` },
          { "@type": "ListItem", "position": 3, "name": "Par ville", "item": `${SITE_URL}/artisans/villes` },
          { "@type": "ListItem", "position": 4, "name": city, "item": `${SITE_URL}/artisans/ville/${slug}` },
        ]
      }
    ],
  };
}

export default function ArtisansCity() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiClient
      .get(`/public/cities/${slug}`)
      .then(({ data }) => setData({ ...data, slug }))
      .catch((e) => setError(e?.response?.data?.detail || "Ville introuvable"));
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#F7F7F9] flex flex-col">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center flex-1">
          <div className="mono text-[11px] uppercase tracking-widest text-zinc-500">// 404</div>
          <h1 className="display text-5xl tracking-tighter mt-3">Ville introuvable</h1>
          <p className="mt-4 text-zinc-600">{error}</p>
          <Link to="/artisans/villes" className="btn-batisur mt-8 inline-block" data-testid="back-to-cities">
            Voir toutes les villes
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#F7F7F9]">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 py-24 mono text-sm text-zinc-500">Chargement…</div>
      </div>
    );
  }

  const stats = [
    { v: String(data.artisans_count), l: "Artisans vérifiés", icon: BadgeCheck },
    { v: String(data.local_completed_missions || 0), l: "Chantiers validés", icon: ShieldCheck },
    { v: String(data.local_certificates || 0), l: "Certificats Batisur", icon: Award },
    { v: fmtCompactEUR(data.local_escrow_total_eur || 0), l: "Sécurisés en séquestre", icon: Building2 },
  ];

  return (
    <div className="min-h-screen bg-[#F7F7F9] flex flex-col" data-testid="artisans-city-page">
      <SEO cfg={buildCitySEO(data)} />
      <Navbar />

      {/* Breadcrumb */}
      <div className="border-b-2 border-[#0A0A0B] bg-white">
        <div className="max-w-7xl mx-auto px-6 py-3 mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-2">
          <Link to="/" className="hover:text-[#FF4D00]">Accueil</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/artisans" className="hover:text-[#FF4D00]">Artisans</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/artisans/villes" className="hover:text-[#FF4D00]">Par ville</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#0A0A0B]">{data.city_label}</span>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-[#0A0A0B] text-white py-16 lg:py-20 border-b-2 border-[#0A0A0B]">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-end">
          <div>
            <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5" /> Annuaire BTP local
            </div>
            <h1 className="display text-4xl lg:text-6xl tracking-tighter mt-3 leading-[0.95]">
              Artisans BTP à <span className="text-[#FF4D00]">{data.city_label}</span>
            </h1>
            <p className="mt-4 text-zinc-300 text-base max-w-xl">
              Plombiers, électriciens, maçons, peintres, carreleurs, chauffagistes, couvreurs, menuisiers, plaquistes à {data.city_label}.
              {data.artisans_count > 0 ? (
                <> {data.artisans_count} professionnel{data.artisans_count > 1 ? "s" : ""} du bâtiment SIRET vérifié{data.artisans_count > 1 ? "s" : ""} Sirene/INSEE.</>
              ) : (
                <> Réseau d'artisans SIRET vérifiés en cours de constitution à {data.city_label}.</>
              )}
              Paiement sécurisé par séquestre Stripe — fonds libérés uniquement après validation par photos horodatées.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/register?role=demandeur" className="btn-batisur" data-testid="city-cta-demandeur">
                Lancer un projet à {data.city_label}
              </Link>
              <Link to="/artisans" className="btn-anthracite border-2 border-white text-white hover:bg-white hover:text-[#0A0A0B]" data-testid="city-cta-explore">
                Voir tous les artisans
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-0 border-2 border-white/20">
            {stats.map((s, i) => (
              <div
                key={s.l}
                className={`p-5 lg:p-6 ${i % 2 === 0 ? "border-r-2 border-white/20" : ""} ${i < 2 ? "border-b-2 border-white/20" : ""}`}
                data-testid={`city-stat-${i}`}
              >
                <s.icon className="w-5 h-5 text-[#FF4D00]" strokeWidth={2.5} />
                <div className="display text-3xl lg:text-4xl tracking-tighter text-white mt-3">{s.v}</div>
                <div className="mono text-[10px] uppercase tracking-widest text-zinc-400 mt-2">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Prospected banner — shown only if city has no artisans yet */}
      {data.prospected && (
        <section className="bg-[#FF4D00] text-white border-b-2 border-[#0A0A0B]" data-testid="prospected-banner">
          <div className="max-w-7xl mx-auto px-6 py-14 lg:py-16 grid lg:grid-cols-[1fr_auto] gap-8 items-center">
            <div>
              <div className="mono text-[11px] uppercase tracking-widest text-white/90 font-semibold">
                // Nouvelle ville · Bientôt disponible
              </div>
              <h2 className="display text-3xl lg:text-4xl tracking-tighter mt-3 leading-tight">
                Batisur arrive à {data.city_label} — <span className="underline decoration-white decoration-[6px] underline-offset-4">soyez le premier artisan</span>
              </h2>
              <p className="mt-4 text-white/90 text-base max-w-2xl">
                Aucun artisan n'est encore inscrit à {data.city_label}, mais les demandeurs de {data.city_label} sont sur Batisur et attendent
                des pros de confiance. <strong>Créez votre profil gratuitement en 60 secondes</strong> et bénéficiez de <strong>30 jours Pack Or offerts</strong> :
                mise en avant annuaire, badge SIRET officiel, Factur-X 2026 inclus.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <Link to="/register?role=artisan&trial=1" data-testid="prospected-cta-artisan">
                <button className="btn-anthracite shadow-[4px_4px_0_#0A0A0B] flex items-center gap-2 w-full">
                  Devenir artisan à {data.city_label} →
                </button>
              </Link>
              <Link to="/register?role=demandeur" data-testid="prospected-cta-demandeur">
                <button className="border-2 border-white text-white px-6 py-3 font-semibold hover:bg-white hover:text-[#FF4D00] transition w-full">
                  Je suis demandeur
                </button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Top artisans */}
      <section className="max-w-7xl mx-auto px-6 py-16 flex-1 w-full">
        {data.prospected ? (
          <>
            <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// En attente d'artisans</div>
            <h2 className="display text-4xl lg:text-5xl tracking-tighter mt-3">
              Les premiers artisans de {data.city_label}
            </h2>
            <p className="mt-3 text-zinc-600 max-w-2xl">
              Batisur constitue actuellement son réseau d'artisans SIRET vérifiés à {data.city_label}.
              En attendant, découvrez les artisans disponibles ailleurs — beaucoup peuvent se déplacer à {data.city_label} pour des chantiers importants.
            </p>
            <div className="mt-8 border-2 border-[#0A0A0B] bg-white p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-[4px_4px_0_#FF4D00]">
              <div>
                <div className="display text-xl">Explorer l'annuaire national</div>
                <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                  Plusieurs artisans acceptent les déplacements grands chantiers
                </div>
              </div>
              <Link to="/artisans" className="btn-batisur" data-testid="prospected-explore">
                Voir tous les artisans →
              </Link>
            </div>

            {/* Notify prospect form */}
            <div className="mt-8">
              <CityProspectForm
                citySlug={data.slug}
                cityLabel={data.city_label}
                context="city"
              />
            </div>
          </>
        ) : (
          <>
            <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// Top des artisans recommandés</div>
            <h2 className="display text-4xl lg:text-5xl tracking-tighter mt-3">
              Les meilleurs pros à {data.city_label}
            </h2>
            <p className="mt-3 text-zinc-600 max-w-2xl">
              Classement par score de recommandation Batisur (badges Or/Premium, SIRET vérifié, certificats émis, missions validées, note moyenne).
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
              {data.top_artisans.map((a) => (
                <Link
                  key={a.id}
                  to={`/artisans/${a.id}`}
                  className="border-2 border-[#0A0A0B] bg-white p-6 hover:bg-[#FF4D00] hover:text-white transition group block"
                  data-testid={`city-artisan-${a.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-wrap">
                      {a.is_gold && <span className="badge-pill bg-amber-500 text-white border-amber-500 inline-flex items-center gap-1"><Crown className="w-3 h-3" /> Or</span>}
                      {a.is_premium && !a.is_gold && <span className="badge-pill bg-[#FF4D00] text-white border-[#FF4D00]">Premium</span>}
                      {a.siret_verified && <span className="badge-pill bg-emerald-600 text-white border-emerald-600 inline-flex items-center gap-1"><BadgeCheck className="w-3 h-3" /> SIRET</span>}
                    </div>
                    <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 group-hover:text-white/80">
                      Score {a.recommendation_score}
                    </div>
                  </div>
                  <div className="display text-2xl tracking-tighter mt-4">{a.name}</div>
                  {a.company && <div className="text-sm mt-1 opacity-80">{a.company}</div>}
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {(a.specialties || []).slice(0, 3).map((s) => (
                      <span key={s} className="mono text-[10px] uppercase tracking-wider border border-current px-2 py-0.5">{s}</span>
                    ))}
                  </div>
                  <div className="border-t-2 border-current/20 mt-5 pt-4 flex items-center justify-between text-xs">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1"><Star className="w-3 h-3" /> {(a.rating_avg || 0).toFixed(1)}</span>
                      <span className="flex items-center gap-1"><Award className="w-3 h-3" /> {a.certificates_count || 0}</span>
                      <span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> {a.completed_missions || 0}</span>
                    </div>
                    <span className="mono uppercase tracking-widest">Voir →</span>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </section>

      {/* SEO content block */}
      <section className="bg-white border-t-2 border-[#0A0A0B]">
        <div className="max-w-4xl mx-auto px-6 py-16 prose-zinc">
          <h2 className="display text-3xl lg:text-4xl tracking-tighter">Pourquoi passer par Batisur à {data.city_label} ?</h2>
          <div className="mt-6 grid sm:grid-cols-2 gap-6 text-sm text-zinc-700 leading-relaxed">
            <div>
              <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">Sécurité financière</div>
              <p className="mt-2">
                À {data.city_label} comme partout en France, le risque #1 d'une rénovation est l'acompte versé puis le chantier abandonné.
                Batisur résout ce problème via un séquestre Stripe : <strong>aucun euro n'est libéré à l'artisan tant que vous n'avez pas validé</strong> le rendu final, photo à l'appui dans le journal de chantier.
              </p>
            </div>
            <div>
              <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">Identité vérifiée</div>
              <p className="mt-2">
                Chaque artisan recommandé à {data.city_label} dispose d'un SIRET officiellement vérifié auprès de la base Sirene/INSEE.
                Plus de devis signés à des entreprises radiées ou fictives — l'identité légale est contrôlée à l'inscription.
              </p>
            </div>
            <div>
              <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">Certificat opposable</div>
              <p className="mt-2">
                À l'issue du chantier, un <strong>Certificat de bonne fin de travaux</strong> PDF est émis automatiquement par Batisur.
                Document opposable lors d'une revente immobilière à {data.city_label} ou en cas de mise en jeu de la garantie décennale.
              </p>
            </div>
            <div>
              <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">Devis et avenants tracés</div>
              <p className="mt-2">
                Les travaux supplémentaires (avenants) suivent le même mécanisme de séquestre. Fini les rallonges de chantier signées
                sur un coin de table : tout est tracé, photo-justifié et payé via Stripe.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-link SEO — Travaux populaires à {ville} × 19 types */}
      <section className="bg-white border-t-2 border-[#0A0A0B]" data-testid="cross-travaux-section">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// Travaux populaires</div>
          <h2 className="display text-3xl lg:text-4xl tracking-tighter mt-3">
            Travaux populaires à {data.city_label}
          </h2>
          <p className="mt-3 text-zinc-700 max-w-3xl">
            Sécurisez chaque type de chantier à {data.city_label} avec un artisan SIRET vérifié.
            Paiement en séquestre Stripe, preuves photos horodatées, garantie décennale.
          </p>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {TRAVAUX_LIST.map((t) => (
              <Link
                key={t.slug}
                to={`/travaux/${t.slug}/${data.slug}`}
                className="group border-2 border-[#0A0A0B] bg-white p-5 hover:bg-[#FF4D00] hover:text-white transition flex items-center justify-between gap-3"
                data-testid={`crosslink-${t.slug}`}
              >
                <div className="min-w-0">
                  <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 group-hover:text-white/80">
                    {t.hero.priceRange}
                  </div>
                  <div className="display text-base leading-tight mt-1 truncate">
                    {t.label} à {data.city_label}
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 flex-shrink-0 group-hover:translate-x-1 transition" />
              </Link>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/travaux" className="mono text-xs uppercase tracking-widest text-[#FF4D00] hover:underline">
              Voir toutes les catégories travaux →
            </Link>
          </div>
        </div>
      </section>

      {/* SEO — Métiers artisans locaux (H2 + 9 H3 pour ranking local Google) */}
      <section className="bg-[#F7F7F9] border-t-2 border-[#0A0A0B]">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// Tous les métiers du BTP</div>
          <h2 className="display text-3xl lg:text-4xl tracking-tighter mt-3">
            Quel artisan chercher à {data.city_label} ?
          </h2>
          <p className="mt-3 text-zinc-700 max-w-3xl">
            Batisur référence tous les corps de métier du bâtiment disponibles à {data.city_label} et dans ses environs.
            Chaque professionnel a son SIRET vérifié à l'inscription et ses avis contrôlés.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { h3: `Plombier ${data.city_label}`, body: `Installation salle de bain complète, cuisine équipée, chauffe-eau, adoucisseur, dépannage fuite d'eau à ${data.city_label}.` },
              { h3: `Électricien ${data.city_label}`, body: `Mise aux normes NF C 15-100, tableau électrique, prises, éclairage LED, domotique connectée à ${data.city_label}.` },
              { h3: `Maçon ${data.city_label}`, body: `Construction neuve, extension, ouverture de mur porteur, ravalement de façade, dallage béton, gros œuvre à ${data.city_label}.` },
              { h3: `Peintre ${data.city_label}`, body: `Peinture intérieure murs & plafonds, façade extérieure, laque, papier peint, enduits décoratifs à ${data.city_label}.` },
              { h3: `Carreleur ${data.city_label}`, body: `Pose carrelage salle de bain, cuisine, sols grand format, mosaïque, marbre à ${data.city_label} et alentours.` },
              { h3: `Chauffagiste ${data.city_label}`, body: `Installation pompe à chaleur, chaudière condensation, climatisation réversible, plancher chauffant à ${data.city_label}. Éligible RGE MaPrimeRénov'.` },
              { h3: `Couvreur ${data.city_label}`, body: `Toiture zinc, ardoise, tuile, étanchéité toit terrasse, isolation combles, ravalement zinguerie à ${data.city_label}.` },
              { h3: `Menuisier ${data.city_label}`, body: `Menuiseries intérieures & extérieures : portes-fenêtres double vitrage, dressing, escalier sur mesure, cuisine à ${data.city_label}.` },
              { h3: `Isolation ${data.city_label}`, body: `Isolation thermique extérieure ITE, combles perdus, murs, planchers à ${data.city_label} — éligible aides MaPrimeRénov' 2026 et CEE.` },
            ].map(({ h3, body }, i) => (
              <div key={i} className="border-l-4 border-[#FF4D00] pl-4 bg-white p-5 shadow-[3px_3px_0_#0A0A0B]" data-testid={`city-metier-${i}`}>
                <h3 className="display text-lg leading-tight">{h3}</h3>
                <p className="text-sm text-zinc-700 mt-2">{body}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 border-2 border-[#0A0A0B] bg-white p-6 lg:p-8 flex flex-wrap items-center justify-between gap-4 shadow-[4px_4px_0_#FF4D00]">
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-[#FF4D00]">// Prêt à démarrer</div>
              <div className="display text-2xl mt-1">Publier une mission à {data.city_label}</div>
              <div className="text-sm text-zinc-600 mt-1">Recevez plusieurs devis en 48h · Aucun engagement · Paiement sécurisé</div>
            </div>
            <Link to="/register?role=demandeur" className="btn-batisur" data-testid={`city-cta-publish-${slug}`}>
              Lancer mon projet →
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
